import { useState } from 'react';
import { ChevronDown, Loader2, Sparkles, Wrench } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';

export interface ThinkingStep {
  type: 'thinking' | 'tool_call';
  text: string;
  /** Which tool-loop iteration produced this step — lets the caller append deltas in-place. */
  iteration?: number;
}

interface ThinkingPanelProps {
  steps: ThinkingStep[];
  /** True while the model is still working — shows a spinner in the (still collapsed) label. */
  active?: boolean;
  /** Elapsed seconds while active; frozen once the turn completes. */
  elapsedSeconds?: number;
}

/**
 * Collapsible "Thinking…" / "Thought for Ns" disclosure, shown above an assistant reply.
 * Used both for the live stream (ChatView) and for historical messages (ChatMessage).
 */
export function ThinkingPanel({ steps, active, elapsedSeconds }: ThinkingPanelProps) {
  // Collapsed by default even while actively streaming — the user opts in to watch the model
  // think rather than having it forced open in front of them.
  const [open, setOpen] = useState(false);

  if (steps.length === 0) {
    return null;
  }

  const label = active
    ? 'Thinking…'
    : elapsedSeconds !== undefined
      ? `Thought for ${elapsedSeconds}s`
      : 'View reasoning';

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="mb-1 max-w-[80%]">
      <CollapsibleTrigger
        data-testid="thinking-panel-toggle"
        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
      >
        {active ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <Sparkles className="h-3 w-3" />
        )}
        <span>{label}</span>
        <ChevronDown className={cn('h-3 w-3 transition-transform', open && 'rotate-180')} />
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-1 rounded-lg border border-border bg-muted/50 px-3 py-2 text-xs text-muted-foreground space-y-1.5">
        {steps.map((step, i) => (
          <div key={i} className="flex items-start gap-1.5">
            {step.type === 'tool_call' ? (
              <Wrench className="h-3 w-3 mt-0.5 shrink-0" />
            ) : (
              <Sparkles className="h-3 w-3 mt-0.5 shrink-0" />
            )}
            <p className="whitespace-pre-wrap break-words">{step.text}</p>
          </div>
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
}
